import { hasSelectedAssets, hasSelectedShifts } from './../filters.js';
import { orderRows } from './../utils.js';

export const UNJUSTIFIED_DEFECT_ID = -1;
export const UNJUSTIFIED_DEFECT_NAME = 'Missing defect';

export const NON_QUALITY_EVENT_DEFECT_ID = -2;
export const NON_QUALITY_EVENT_DEFECT_NAME = 'Others (excludes quality events)';

/**
 * Get the ID to use for a defect row, considering that some are unjustified or not related to a quality event.
 * @param {Object} record
 * @return {Number} defectID
 */
export function getDefectID(record) {
    if (record.quality_event_count === 0) {
        return NON_QUALITY_EVENT_DEFECT_ID;
    } else if (record.defect_id === null && record.quality_event_count > 0) {
        return UNJUSTIFIED_DEFECT_ID;
    }

    return record.defect_id;
}

/**
 * Get the name of the defect row to use, considering that some are unjustified or not related to a quality event.
 * @param {Object} record
 * @return {String} defectName
 */
export function getDefectName(record) {
    const defectID = getDefectID(record);
    if (defectID === NON_QUALITY_EVENT_DEFECT_ID) {
        return NON_QUALITY_EVENT_DEFECT_NAME;
    } else if (defectID === UNJUSTIFIED_DEFECT_ID) {
        return UNJUSTIFIED_DEFECT_NAME;
    }

    return record.defect_name;
}

/**
 * Determine whether to include a record in the report.
 * @param {Object} record
 * @param {Object} filters
 * @return {Boolean}
 *
 * The filter should be of the following form:
 * { minor: (bool), major: (bool), assetIds: (set), shiftIds: (set) }
 */
export function isIncluded(record, filters) {
    return (
        hasSelectedAssets(record, filters) && hasSelectedShifts(record, filters)
    );
}

export function getDefectRow(report, record, groupBy) {
    const defectId = getDefectID(record);
    if (!(defectId in report.defects)) {
        if (groupBy === 'assets') {
            report.defects[defectId] = {
                id: defectId,
                name: getDefectName(record),
                qualityEventIDs: new Set(),
                ...getDefaultRowValues(),
                assets: {}
            };
        } else {
            report.defects[defectId] = {
                id: defectId,
                name: getDefectName(record),
                qualityEventIDs: new Set(),
                ...getDefaultRowValues(),
                products: {}
            };
        }
    }
    return report.defects[defectId];
}

export function getDefectSubRow(defectRow, record, groupBy) {
    const assetId = record.asset_id;
    const productId = record.product_id;
    if (groupBy === 'assets') {
        if (!(assetId in defectRow.assets)) {
            defectRow.assets[assetId] = {
                id: assetId,
                name: record.asset_name,
                defectId: record.defect_id,
                defectName: record.defect_name,
                ...getDefaultRowValues()
            };
        }
        return defectRow.assets[assetId];
    } else {
        if (!(productId in defectRow.products)) {
            defectRow.products[productId] = {
                id: productId,
                name: record.product_name,
                assetId: record.asset_id,
                defectId: record.defect_id,
                defectName: record.defect_name,
                ...getDefaultRowValues()
            };
        }
        return defectRow.products[productId];
    }
}

/**
 * Parse quality event IDs from a string into a set.
 * @param {Object} quality_event_ids - The defect column containing quality event IDs.
 * @return {Set} A set of quality event IDs.
 */
function parseQualityEventIDs(quality_event_ids) {
    if (quality_event_ids === '') {
        return new Set();
    }

    return new Set(quality_event_ids.split(',').map(id => parseInt(id, 10)));
}

/**
 * Build the quality report.
 * @param {Object} record
 * @param {Object} filters
 * @return {Object} groupBy - The grouping method for the report (either 'assets' or 'products').
 *
 * The filter should be of the following form:
 * { minor: (bool), major: (bool), assetIds: (set), shiftIds: (set) }
 */
export function buildQualityReport(records, filters, groupBy) {
    const report = {
        defects: {},
        totals: getDefaultRowValues()
    };

    if (records !== null) {
        for (const record of records) {
            if (isIncluded(record, filters)) {
                const defectRow = getDefectRow(report, record, groupBy);
                const defectSubRow = getDefectSubRow(
                    defectRow,
                    record,
                    groupBy
                );
                if (record.rejected_production !== null) {
                    defectSubRow.rejectedProduction +=
                        record.rejected_production;
                    defectRow.rejectedProduction += record.rejected_production;
                    report.totals.rejectedProduction +=
                        record.rejected_production;
                }

                const qualityEventIDs = parseQualityEventIDs(
                    record.quality_event_ids
                );

                // check if the quality event IDs has already been accounted for in the totals
                // If not, include them.
                for (const id of qualityEventIDs) {
                    if (!defectRow.qualityEventIDs.has(id)) {
                        defectRow.nbEvents += 1;
                        defectSubRow.nbEvents += 1;
                        report.totals.nbEvents += 1;

                        defectRow.qualityEventIDs.add(id);
                    }
                }
            }
        }
    }
    return report;
}

/**
 * Populate the rows in the quality report table
 * @param {Object} defects
 * @param {String} groupBy
 * @param {Object} table
 * @param {String} orderBy
 * @param {String} descending
 */
function populateRows(defects, groupBy, table, orderBy, descending) {
    for (const [defectID, defect] of Object.entries(defects)) {
        const row = {
            id: parseInt(defectID),
            name: defect.name,
            nbEvents: defect.nbEvents,
            rejectedProduction: defect.rejectedProduction
        };

        if (groupBy === 'assets') {
            row['assets'] = [];
            for (const assetRowData of Object.values(defect.assets)) {
                const assetRow = {
                    id: assetRowData.id,
                    name: assetRowData.name,
                    defectId: assetRowData.defectId,
                    defectName: assetRowData.defectName,
                    nbEvents: assetRowData.nbEvents,
                    rejectedProduction: assetRowData.rejectedProduction
                };
                orderRows(row.assets, orderBy, descending);
                row.assets.push(assetRow);
            }
        } else {
            row['products'] = [];
            for (const productRowData of Object.values(defect.products)) {
                const productRow = {
                    id: productRowData.id,
                    name: productRowData.name,
                    assetId: productRowData.assetId, // Include asset ID for justifying using the missing defect button
                    defectId: productRowData.defectId,
                    defectName: productRowData.defectName,
                    nbEvents: productRowData.nbEvents,
                    rejectedProduction: productRowData.rejectedProduction
                };
                orderRows(row.products, orderBy, descending);
                row.products.push(productRow);
            }
        }
        table.rows.push(row);
    }
}

/**
 * Builds a table given a quality report
 * @param {Object} report
 * @param {String} groupBy
 * @param {String} orderBy
 * @param {Boolean} descending
 * @return {Object} table data
 */
export function buildTable(report, groupBy, orderBy, descending = true) {
    const table = {
        rows: [],
        totals: report.totals
    };

    populateRows(report.defects, groupBy, table, orderBy, descending);
    orderRows(table.rows, orderBy, descending);
    return table;
}

/**
 * Get default values for a new asset/product row in the performance table.
 * @return {Object}
 */
export function getDefaultRowValues() {
    return {
        nbEvents: 0,
        rejectedProduction: null
    };
}
